import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { useConfiguratorPopupContext } from "providers";
import { useIsAuthenticated, useUser } from "stores/user";
import { useProject, useProjectActions } from "stores/project";
import { UncontrolledTooltip } from "reactstrap";
import { useTranslate } from "hooks";
import { useHasProductChanged } from "stores/product";
import { tenants } from "config/tenants";
import { lastSelectedUserLangLSKey } from "variables/localStorageItemsNames";
import type { TenantKey } from "interfaces/shared.types";
import { Icon } from "../../Icon/Icon";
import { NavRightSidebar } from "./NavRightSidebar";
import { LoginButton } from "../LoginButton";
import { Logo } from "../Logo";

import s from "./navConfigurator.module.scss";
import { useIsLoaderLine, useIsFetching } from "stores/loadingStateStore";
import { useIsSameProj, useLanguage, useSetLanguage } from "stores/globalStore";

type NavConfiguratorProps = { homepage: Scrivito.Obj; device: string };

export const NavConfigurator = Scrivito.connect(
  ({ homepage, device }: NavConfiguratorProps) => {
    const isAuthenticated = useIsAuthenticated();
    const user = useUser();

    const translate = useTranslate();
    const project = useProject();
    const { clear: clearProject, refetch: refetchProject } =
      useProjectActions();
    const urlTitle = new URLSearchParams(window.location.search).get(
      "originalProjectName",
    );
    const title = urlTitle || project?.title;

    const isFetching = useIsFetching();
    const isLoaderLine = useIsLoaderLine();

    // TODO GET_INITIAL_PROJECT_STATE ANOTHER ONE BUAUTIFUL STUFF
    const isSameProj = useIsSameProj();
    const setLang = useSetLanguage();
    const selectedLang = useLanguage();
    const isUpdateButtonActive = useHasProductChanged();
    const { showConfiguratorPopup } = useConfiguratorPopupContext()!;
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    const supportedLanguages = useMemo(
      () => tenants[(user?.tenant as TenantKey) || "SC"]?.language || [],
      [user],
    );

    useEffect(() => {
      if (!supportedLanguages.length) {
        return;
      }
      if (!selectedLang) {
        return;
      }

      const allowedValues = supportedLanguages.map((l) => l.value);
      if (allowedValues.includes(selectedLang)) {
        return;
      }

      const tenantKey = (user?.tenant as TenantKey) || "SC";
      const fallbackLanguage =
        tenants[tenantKey as keyof typeof tenants]?.default_language ||
        supportedLanguages[0]?.value;

      if (!fallbackLanguage || fallbackLanguage === selectedLang) {
        return;
      }

      localStorage.setItem(lastSelectedUserLangLSKey, fallbackLanguage);
      setLang(fallbackLanguage);
    }, [supportedLanguages, selectedLang, setLang, user?.tenant]);

    const onToggleSideBar = useCallback(() => {
      setSidebarOpen((val) => !val);
    }, []);

    const handleprojectRefetch = useCallback(() => {
      const isWizardPage = window.location.href.includes("wizard");
      if (isWizardPage) {
        refetchProject();
      }
    }, [refetchProject]);

    const navigateToProjectConfigurator = useCallback(() => {
      if (!homepage || !project?.id) {
        return;
      }

      handleprojectRefetch();
      Scrivito.navigateTo(
        homepage.get("projectConfiguratorPage") as Scrivito.Obj,
        {
          projectId: project?.id,
        },
      );
    }, [handleprojectRefetch, homepage, project?.id]);

    const handleProjectTitleClick = useCallback(
      (event: React.MouseEvent) => {
        if (isUpdateButtonActive) {
          event.preventDefault();
          showConfiguratorPopup(navigateToProjectConfigurator);
          return;
        }

        handleprojectRefetch();
      },
      [
        handleprojectRefetch,
        isUpdateButtonActive,
        navigateToProjectConfigurator,
        showConfiguratorPopup,
      ],
    );

    const goHome = useCallback(() => {
      if (isAuthenticated) {
        if (!homepage) {
          return;
        }
        const home = homepage.get("welcomePage");
        const navigate = () => {
          clearProject();
          Scrivito.navigateTo(home as Scrivito.Obj);
        };

        if (isUpdateButtonActive) {
          showConfiguratorPopup(navigate);
        } else {
          navigate();
        }
      }
    }, [
      isAuthenticated,
      homepage,
      clearProject,
      isUpdateButtonActive,
      showConfiguratorPopup,
    ]);

    const handleLogoClick = useCallback(
      (
        event: React.MouseEvent,
        link: Scrivito.Link,
        resetProjectData: () => void,
      ) => {
        if (isUpdateButtonActive) {
          event.preventDefault();
          showConfiguratorPopup(() => {
            resetProjectData();
            Scrivito.navigateTo(link);
          });
          return;
        }

        resetProjectData();
      },
      [isUpdateButtonActive, showConfiguratorPopup],
    );

    if (!homepage) {
      return null;
    }

    return (
      <>
        <header
          data-testid="nav-configurator"
          className={cx(
            "py-3",
            s.configuratorHeader,
            s.configuratorHeaderWithGrayBar,
            {
              [s.loader]: isLoaderLine && isFetching,
            },
          )}
        >
          <div className="d-flex justify-content-between px-4">
            <div className="d-flex align-items-center">
              <Logo
                homepage={homepage}
                imgClass={`m-0 ${s.logoIcon} w-auto me-2 me-md-3 me-xl-4`}
                isConfigLog
                onClick={handleLogoClick}
              />
              <h3 className="mb-0 text-uppercase text-gray-500">
                {homepage.get("configuratorNavTitle") as string}
                {!isSameProj && (
                  <>
                    <span id="beta" className="text-blue-300 ps-2">
                      BETA
                    </span>
                    <UncontrolledTooltip target="beta" placement="bottom">
                      {translate("TROX_BETA_INFO_TEXT")}
                    </UncontrolledTooltip>
                  </>
                )}
              </h3>
              {project?.id && title && (
                <div
                  className={cx(
                    "d-flex align-items-center mx-2 px-2 border-start border-gray-400",
                  )}
                >
                  <Scrivito.LinkTag
                    to={homepage.get("projectConfiguratorPage") as Scrivito.Obj}
                    params={{ projectId: project?.id }}
                    className="d-flex flex-no-wrap"
                    onClick={handleProjectTitleClick}
                  >
                    <Icon
                      iconName="folder-regular"
                      className="icon-16 text-gray-500 me-2"
                    />

                    <div className="font-bold text-gray-500">{title}</div>
                  </Scrivito.LinkTag>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center">
              {!isSameProj && (
                <div className="position-relative me-6">
                  {/* TODO: Rows below are for future when a shop there's in configurator app */}
                  {/* <div */}
                  {/*  className={cx( */}
                  {/*    ` */}
                  {/*  position-absolute */}
                  {/*  d-flex */}
                  {/*  justify-content-center */}
                  {/*  align-items-center */}
                  {/*  w-20 */}
                  {/*  h-20 */}
                  {/*  rounded-circle */}
                  {/*  bg-blue-300 */}
                  {/* `, */}
                  {/*    s.cartBubble */}
                  {/*  )} */}
                  {/* > */}
                  {/*  <span className={cx("d-flex align-items-center text-white", s.cartNumber)}>18</span> */}
                  {/* </div> */}
                  <button
                    data-testid="NavConfigurator_shopping-cart-btn"
                    className="p-0 bg-transparent border-0"
                  >
                    <a
                      className="text-decoration-none"
                      href="https://shop.trox.de/"
                    >
                      {/* TODO: forthcoming feature (hidden acc US-19953) hidden with className='d-none' */}
                      <Icon
                        iconName="shopping-cart-light"
                        className="icon-32 text-blue-800 d-none"
                        title="shopping-cart"
                      />
                    </a>
                  </button>
                </div>
              )}
              {device !== "mobile" && (
                <>
                  <LoginButton />
                  {!isSameProj && (
                    <>
                      <Scrivito.LinkTag
                        onClick={goHome}
                        className="bg-transparent p-0 text-decoration-none ms-md-4 ms-xl-6"
                        data-testid="home-icon"
                      >
                        <Icon
                          iconName="home-header"
                          className="icon-32 text-blue-800"
                          title="home"
                        />
                      </Scrivito.LinkTag>

                      <button
                        data-testid="hamburger-icon"
                        onClick={onToggleSideBar}
                        className="bg-transparent border-0 p-0 ms-4"
                        title="bars-button"
                      >
                        <Icon
                          iconName="bars-regular"
                          className="icon-32 text-gray-400"
                          title="bars"
                        />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </header>
        {isSidebarOpen && (
          <NavRightSidebar
            onToggleSideBar={onToggleSideBar}
            isOpen={isSidebarOpen}
          />
        )}
      </>
    );
  },
);
